import React, { useEffect, useState, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import SearchBox from 'components/common/SearchBox';
import {
  RecordingsTable,
  getRecordingsTableColumns
} from 'components/tables/RecordingsTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import * as noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import { fetchHVASearchCalls } from 'services/hvasearchcall.service';
import HVASpinner from 'components/base/HVASpinner';

// Extender dayjs con el plugin UTC
dayjs.extend(utc);

interface Call {
  servicename: string;
  servicebrand: string;
  campaignname: string;
  callid: string;
  campaignid: number;
  audiosource: string;
  agent: string;
  accountref: string;
  customerref: string;
  customerfullname: string;
  phonenumber: string;
  calldatetime: string;
  duration: string;
}

interface CampaignRecordingExplorerCallsProps {
  campaignid: number; // campaignid es obligatorio
}

const CampaignTodayCalls: React.FC<CampaignRecordingExplorerCallsProps> = ({
  campaignid
}) => {
  const [data, setData] = useState<Call[]>([]);
  const [maxDuration, setMaxDuration] = useState(600);
  const [durationRange, setDurationRange] = useState<[number, number]>([
    0,
    maxDuration
  ]);
  const [loading, setLoading] = useState(true); // Estado para el spinner
  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderInstanceRef = useRef<noUiSlider.API | null>(null);
  const [minDurationInput, setMinDurationInput] = useState<string>(
    durationRange[0].toString()
  );
  const [maxDurationInput, setMaxDurationInput] = useState<string>(
    durationRange[1].toString()
  );
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  // Crear las columnas con el callback para controlar el estado del modal
  const columns = useMemo(
    () => getRecordingsTableColumns(isOpen => setIsAudioModalOpen(isOpen)),
    []
  );

  // Actualizar el slider de duración
  useEffect(() => {
    if (sliderRef.current) {
      // Limpiar el slider anterior si existe
      if (sliderInstanceRef.current) {
        sliderInstanceRef.current.destroy();
      }

      // Crear nuevo slider
      sliderInstanceRef.current = noUiSlider.create(sliderRef.current, {
        start: durationRange,
        connect: true,
        range: {
          min: 0,
          max: maxDuration || 600
        },
        step: 1
      });

      // Actualizar las etiquetas mientras el slider se arrastra
      sliderInstanceRef.current.on('update', (values: (string | number)[]) => {
        const newMin = parseInt(values[0] as string, 10);
        const newMax = parseInt(values[1] as string, 10);

        setDurationRange([newMin, newMax]);
        setMinDurationInput(newMin.toString());
        setMaxDurationInput(newMax.toString());
      });

      return () => {
        if (sliderInstanceRef.current) {
          sliderInstanceRef.current.destroy();
          sliderInstanceRef.current = null;
        }
      };
    }
  }, [maxDuration]);

  // Obtener los datos de las llamadas
  useEffect(() => {
    const fetchData = async () => {
      // No actualizar si hay un modal de audio abierto
      if (isAudioModalOpen) {
        return;
      }

      try {
        // Calcular fecha actual sin hora
        const startDate = dayjs().startOf('day').utc().format();

        // Para producción - Bórrame y pon lo de arriba
        // eslint-disable-next-line
        // const startDate = dayjs().startOf('day').subtract(600, 'days').utc().format();

        // Llamar al servicio con solo startDate
        const calls = await fetchHVASearchCalls({
          campaign: campaignid.toString(),
          startDate
        });

        // Transformar los datos para adaptarlos a la estructura esperada en la tabla
        const transformedCalls = calls.map((call: Call) => ({
          servicename: call.servicename,
          servicebrand: call.servicebrand,
          campaignname: call.campaignname,
          callid: call.callid,
          campaignid: call.campaignid,
          audiosource: call.audiosource,
          agent: call.agent,
          accountref: call.accountref,
          customerref: call.customerref,
          customerfullname: call.customerfullname,
          phonenumber: call.phonenumber,
          calldatetime: dayjs(call.calldatetime).format('DD-MM-YYYY HH:mm:ss'),
          duration: call.duration.toString()
        }));
        setData(transformedCalls);
        setLoading(false);

        if (calls.length === 0) {
          // Si no hay registros, establecer el máximo en 600
          setMaxDuration(0);
        } else {
          // Calcular la duración máxima
          const maxDurationValue = calls.reduce((max: number, call: Call) => {
            const duration = parseInt(call.duration, 10);
            return duration > max ? duration : max;
          }, 0);
          setMaxDuration(maxDurationValue);

          // Actualizar el rango de duración con el nuevo valor máximo
          setDurationRange([0, maxDurationValue]);
        }
      } catch (error) {
        console.error('Error fetching calls:', error);
        setLoading(false);
      }
    };

    fetchData(); // Fetch data initially

    setMinDurationInput('0');
    setMaxDurationInput(maxDuration.toString());

    // Configurar el intervalo de actualización
    const intervalId = setInterval(fetchData, 60000); // Actualizar cada 60 segundos

    // Limpiar el intervalo cuando el componente se desmonte
    return () => clearInterval(intervalId);
  }, [campaignid, isAudioModalOpen]);

  const filteredData = useMemo(() => {
    return data.filter(call => {
      const duration = parseInt(call.duration, 10);
      return duration >= durationRange[0] && duration <= durationRange[1];
    });
  }, [data, durationRange]);

  const table = useAdvanceTable({
    data: filteredData,
    columns: columns,
    pageSize: 9,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleMinDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMinDurationInput(e.target.value);
  };

  const handleMaxDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaxDurationInput(e.target.value);
  };

  const handleMinDurationBlur = () => {
    let newMin = parseInt(minDurationInput, 10);

    // Validaciones
    if (isNaN(newMin)) newMin = 0;
    if (newMin < 0) newMin = 0;
    if (newMin > durationRange[1]) newMin = durationRange[1];

    // Actualizar el slider y el input
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.set([newMin, durationRange[1]]);
    }
  };

  const handleMaxDurationBlur = () => {
    let newMax = parseInt(maxDurationInput, 10);

    // Validaciones
    if (isNaN(newMax)) newMax = maxDuration;
    if (newMax > maxDuration) newMax = maxDuration;
    if (newMax < durationRange[0]) newMax = durationRange[0];

    // Actualizar el slider y el input
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.set([durationRange[0], newMax]);
    }
  };

  const handleDurationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Pierde el foco para activar el evento onBlur
    }
  };

  return (
    <div>
      {loading ? (
        <div className="d-flex justify-content-center align-items-center">
          <HVASpinner />
        </div>
      ) : (
        <div className="mb-9">
          <AdvanceTableProvider {...table}>
            <div className="mb-4">
              <div className="d-flex flex-wrap gap-3">
                <SearchBox
                  placeholder="Search calls"
                  onChange={handleSearchInputChange}
                  onKeyDown={handleSearchKeyPress}
                />
                <div className="d-flex align-items-center">
                  <span className="fs-9 px-2 pe-1">Call Duration (sec.):</span>
                  <input
                    type="text"
                    className="d-flex fs-9 fw-bold justify-content-end text-end"
                    style={{
                      width: '30px',
                      border: 'none',
                      background: 'transparent',
                      outline: 'none'
                    }}
                    value={minDurationInput}
                    onChange={handleMinDurationChange}
                    onBlur={handleMinDurationBlur}
                    onKeyDown={handleDurationKeyDown}
                    aria-label="Minimum duration in seconds"
                    title="Minimum duration in seconds"
                  />
                  <div
                    className="noUi-primary-lighter noUi-handle-primary noUi-slider-slim noUi-handle-circle"
                    ref={sliderRef}
                    style={{ width: '150px', margin: '12px 12px' }}
                  ></div>
                  <input
                    type="text"
                    className="fs-9 fw-bold text-start"
                    style={{
                      width: '30px',
                      border: 'none',
                      background: 'transparent',
                      outline: 'none'
                    }}
                    value={maxDurationInput}
                    onChange={handleMaxDurationChange}
                    onBlur={handleMaxDurationBlur}
                    onKeyDown={handleDurationKeyDown}
                    aria-label="Maximum duration in seconds"
                    title="Maximum duration in seconds"
                  />
                </div>
              </div>
            </div>

            <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 border-translucent position-relative top-1">
              <RecordingsTable />
            </div>
          </AdvanceTableProvider>
        </div>
      )}
    </div>
  );
};

export default CampaignTodayCalls;
